/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery } from "@tanstack/react-query";
import { trackAssetFormOutcome } from "../../analytics";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import useAuthHttp from "../../hooks/useAuthHttp";
import { IAssetDetailsResponse, IAssetInfo, IAssetTypesResponse } from "../../models/asset";
import TreasureBondsSearchSelector, { ITreasureList, ITreasureSearchResponse } from "../TreasureBoundsSearchSelector";
import { useFormik } from "formik";
import { groupAssetListTypeByGroupName } from "../../commons/group-assets-list-type";
import { ASSET_MIN_PURCHASE_DATE, yupAssetPurchaseDate } from "../../commons/assetPurchaseDate";
import * as Yup from "yup";
import View from "../Container";
import { Toast } from "primereact/toast";
import Nav, { NavItem } from "../Nav";
import Button from "../Button";
import Text from "../Text";
import FormField from "../FormField";
import { Calendar } from "primereact/calendar";
import BankSearchSelector from "../BankSearchSelector";
import { InputNumber } from "primereact/inputnumber";
import { useHomeContext } from "../../contexts/HomeContext";
import Show from "../Show";
import MainLoader from "../../layout/MainLoader";
import Container from "../Container";
import { IExtractedInvestment } from "../../models/extractedData";
import { assetSourceFieldForDirpfImport } from "../../commons/dirpfImportMeta";
import { treasurePurchaseQuote } from "../../commons/treasure-quote";
import { isTesouroReserva } from "../../commons/treasure-reserva";

export interface ITreasureBoundsHandleForm {
    treasure?: ITreasureList;
    dueDate?: Date;
    negotiatingBank?: {
        id: string;
        name: string;
    };
    purchaseDate?: Date;
    operationCosts?: {
        brokerage?: number;
        others?: number;
    };
    purchaseValue?: number;
    fee?: number;
    /** PU do dia da compra: quantidade = purchaseValue / unitValue na core-api. */
    unitValue?: number;
}

type QuoteStatus = "idle" | "found" | "missing";

export interface ITreasureBoundsHandleFormProps {
    onSuccessRegister(assetInfo: ITreasureBoundsHandleForm | null): void;
    goBack(): void;
    assetId?: string;
    initialData?: IExtractedInvestment;
}

const TreasureBoundsHandleForm = (props: ITreasureBoundsHandleFormProps) => {
    const http = useAuthHttp();
    const [isEdit, setIsEdit] = useState(false);
    const [quoteStatus, setQuoteStatus] = useState<QuoteStatus>("idle");
    const [quoteDate, setQuoteDate] = useState<string | null>(null);
    const toast = useRef<Toast | null>(null);
    const homeContext = useHomeContext();

    const upsertAssetMutation = useMutation({
        mutationFn: async (form: ITreasureBoundsHandleForm) => {
            const body = {
                asset_id: assetListTypeGrouped
                    .find(listType => listType.groupName == "Tesouro Direto")!.names[0].id,
                asset_type: "TB",
                asset_fin_negotiating_id: form!.negotiatingBank!.id,
                asset_purchase_date: moment(form.purchaseDate).format("YYYY-MM-DD HH:mm:ss"),
                asset_due_date: moment(form.dueDate).format("YYYY-MM-DD HH:mm:ss"),
                asset_tb_details: {
                    tb_id: form.treasure!.id,
                    tb_pu_value_prch: form?.purchaseValue,
                    tb_oc_brokerage: form?.operationCosts?.brokerage,
                    tb_oc_others: form?.operationCosts?.others,
                    tb_rate_pre_plus: form.fee,
                    tb_pu_value_vna: form.unitValue
                },
                ...(!isEdit ? (assetSourceFieldForDirpfImport(props.initialData) ?? {}) : {})
            };

            if (isEdit) {
                return await http.doPatch<any, IAssetInfo>(`/asset/${props.assetId}`, body);
            }

            return await http.doPost<any, IAssetInfo>("/asset/insert", body);
        }
    });

    const assetDetailsQuery = useQuery({
        queryKey: ["assetDetails", props.assetId],
        gcTime: 0,
        queryFn: async () => {
            if (!props.assetId) {
                return {
                    asset_info: null
                };
            }

            return await http.doGet<IAssetDetailsResponse>(`/asset/${props.assetId}`)
        }
    });

    const loadTaxMutation = useMutation({
        mutationFn: async ({ treasureId, date }: { treasureId: string, date: Date }) => {
            return await http.doGet<ITreasureSearchResponse>(`/treasure/br/fee/${treasureId}?date=${moment(date).format("YYYY-MM-DD")}`);
        }
    });

    const assetListTypeQuery = useQuery({
        queryKey: [`assetListType`],
        queryFn: async () =>
            await http.doGet<IAssetTypesResponse>('asset/list_types')
    });
    const assetListTypeGrouped = groupAssetListTypeByGroupName(assetListTypeQuery.data?.asset_list ?? []);

    const formik = useFormik<ITreasureBoundsHandleForm>({
        initialValues: {
            treasure: undefined,
            dueDate: undefined,
            negotiatingBank: undefined,
            purchaseDate: undefined,
            operationCosts: {
                brokerage: 0,
                others: 0,
            },
            purchaseValue: 0,
            fee: 0,
            unitValue: 0
        },
        validationSchema: Yup
            .object()
            .shape({
                treasure: Yup
                    .object()
                    .required("Campo obrigatório"),
                dueDate: Yup
                    .date()
                    .required("Campo obrigatório"),
                negotiatingBank: Yup
                    .object()
                    .required("Campo obrigatório"),
                purchaseDate: yupAssetPurchaseDate(),
                operationCosts: Yup
                    .object()
                    .shape({
                        brokerage: Yup.string().optional(),
                        others: Yup.string().optional()
                    }),
                purchaseValue: Yup
                    .number()
                    .min(1, "É necessário informar um valor")
                    .required("Campo obrigatório"),
                fee: Yup
                    .string()
                    .required("Campo obrigatório"),
                unitValue: Yup
                    .number()
                    .typeError("O preço unitário (PU) não pode ser zero")
                    .test(
                        "unit-value",
                        "O preço unitário (PU) não pode ser zero",
                        function (value) {
                            return isTesouroReserva(this.parent.treasure) || (value ?? 0) > 0;
                        }
                    ),
            }),
        onSubmit: (form) => send(form)
    });

    const isReserva = isTesouroReserva(formik.values.treasure);

    const loadQuote = async (
        treasure: { id?: string, TrsrBdType?: { nm?: string } | null } | undefined,
        date: Date | null | undefined,
        { overwriteFee, overwritePurchaseValue }: { overwriteFee: boolean, overwritePurchaseValue: boolean }
    ) => {
        const treasureId = treasure?.id;
        if (!treasureId || !date || isTesouroReserva(treasure)) {
            return;
        }

        try {
            const quote = treasurePurchaseQuote(await loadTaxMutation.mutateAsync({ treasureId, date }));
            if (!quote) {
                setQuoteStatus("missing");
                return;
            }

            formik.setFieldValue("unitValue", quote.unitValue);
            if (overwriteFee) {
                formik.setFieldValue("fee", quote.rate?.toLocaleString());
            }
            if (overwritePurchaseValue) {
                formik.setFieldValue("purchaseValue", quote.unitValue);
            }
            setQuoteDate(String(quote.date));
            setQuoteStatus("found");
        } catch {
            setQuoteStatus("missing");
        }
    }

    useEffect(() => {
        const body = document.getElementsByTagName("body")[0];
        body.style.overflow = "hidden";

        return () => {
            body.style.overflow = "auto";
        }
    }, []);

    useEffect(() => {
        if (!assetDetailsQuery.data?.asset_info) {
            setIsEdit(false);
            return;
        }

        setIsEdit(true);

        const assetInfo = assetDetailsQuery.data.asset_info;
        const storedUnitValue = assetInfo.asset_tb_details.tb_pu_value_vna ?? 0;
        if (storedUnitValue <= 0) {
            loadQuote(assetInfo.asset_tb_details.treasure, new Date(assetInfo.asset_purchase_date), { overwriteFee: false, overwritePurchaseValue: false });
        }

        formik.setValues({
            treasure: {
                id: assetDetailsQuery.data.asset_info.asset_tb_details.treasure.id,
                TrsrBd: assetDetailsQuery.data.asset_info.asset_tb_details.treasure.TrsrBd as any,
                TrsrBdType: assetDetailsQuery.data.asset_info.asset_tb_details.treasure.TrsrBdType as any,
                SelicCode: assetDetailsQuery.data.asset_info.asset_tb_details.treasure.SelicCode,
                PrcgLst: assetDetailsQuery.data.asset_info.asset_tb_details.treasure.PrcgLst
            },
            dueDate: new Date(assetDetailsQuery.data.asset_info.asset_due_date),
            negotiatingBank: {
                id: assetDetailsQuery.data.asset_info.asset_fin_issue.id,
                name: assetDetailsQuery.data.asset_info.asset_fin_issue.inst_name,
            },
            purchaseDate: new Date(assetDetailsQuery.data.asset_info.asset_purchase_date),
            operationCosts: {
                brokerage: assetDetailsQuery.data.asset_info.asset_tb_details.tb_oc_brokerage,
                others: assetDetailsQuery.data.asset_info.asset_tb_details.tb_oc_others,
            },
            purchaseValue: assetDetailsQuery.data.asset_info.asset_tb_details.tb_pu_value_prch,
            fee: assetDetailsQuery.data.asset_info.asset_tb_details.tb_rate_pre_plus,
            unitValue: storedUnitValue
        });
    }, [assetDetailsQuery.data?.asset_info]);

    useEffect(() => {
        if (!props.initialData?.asset_tb_details) {
            return;
        }
        const data = props.initialData;
        const importedPurchaseDate = data.asset_purchase_date ? moment(data.asset_purchase_date).toDate() : undefined;
        loadQuote(data.asset_tb_details?.treasure, importedPurchaseDate, {
            overwriteFee: !data.asset_tb_details?.tb_rate_pre_plus,
            overwritePurchaseValue: false
        });
        formik.setValues({
            treasure: {
                id: data.asset_tb_details?.treasure?.id || "",
                TrsrBd: data.asset_tb_details?.treasure?.TrsrBd as any,
                TrsrBdType: data.asset_tb_details?.treasure?.TrsrBdType as any,
                SelicCode: data.asset_tb_details?.treasure?.SelicCode,
                PrcgLst: data.asset_tb_details?.treasure?.PrcgLst || []
            },
            purchaseDate: importedPurchaseDate,
            dueDate: data.asset_due_date ? moment(data.asset_due_date).toDate() : undefined,
            purchaseValue: data.asset_tb_details?.tb_pu_value_prch || data.asset_current_value || 0,
            fee: data.asset_tb_details?.tb_rate_pre_plus || 0,
            unitValue: 0,
            negotiatingBank: data.asset_fin_issue ? {
                id: data.asset_fin_issue.id,
                name: data.asset_fin_issue.inst_name
            } : undefined
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.initialData]);

    const send = async (form: ITreasureBoundsHandleForm) => {
        if (!form) {
            return;
        }

        try {
            const assetInfo = await upsertAssetMutation.mutateAsync(form);

            if (assetInfo) {
                props.onSuccessRegister(form);
                await homeContext.refresh();
            } else {
                if (!isEdit) trackAssetFormOutcome('treasure_bounds', false, 'failed');
                toast.current?.show({
                    severity: "error",
                    summary: "Falha ao cadastrar investimento"
                });
            }
        } catch (err) {
            if (!isEdit) trackAssetFormOutcome('treasure_bounds', false, 'failed');
            toast.current?.show({
                severity: "error",
                summary: (err as Error).message
            });
        }
    }

    return (
        <>
            <Toast ref={toast} />
            <Show when={assetDetailsQuery.isLoading}>
                <Container center fullWidth>
                    <MainLoader fullHeight={false} minHeight="300px" />
                </Container>
            </Show>

            <Show when={!assetDetailsQuery.isLoading}>
                <View flexColumn gap={3}>
                    <h2 className="m-0">Tesouro Direto</h2>

                    <Nav>
                        <NavItem
                            id="go-back"
                            showPrefixIcon
                            onSelectOption={() => props.goBack()}
                        >
                            Voltar
                        </NavItem>
                    </Nav>

                    <TreasureBondsSearchSelector
                        hasError={!!formik.errors.treasure && formik.touched.treasure}
                        error={formik.errors.treasure}
                        selected={formik.values.treasure}
                        onSelect={(treasure) => {
                            formik.setFieldValue("treasure", treasure);
                            formik.setFieldValue(
                                "dueDate",
                                !treasure ? undefined : moment(treasure?.TrsrBd?.mtrtyDt).toDate()
                            );
                            if (isTesouroReserva(treasure)) {
                                formik.setFieldValue("fee", 0);
                                formik.setFieldValue("unitValue", 0);
                                setQuoteStatus("idle");
                                return;
                            }
                            loadQuote(treasure ?? undefined, formik.values.purchaseDate, { overwriteFee: true, overwritePurchaseValue: !isEdit });
                        }}
                    />

                    {
                        formik.values?.treasure?.id
                            ? (
                                <View flexColumn>
                                    <Text>
                                        Índice de correção
                                    </Text>

                                    <View className="mt-1">
                                        <View className="flex flex-row items-center">
                                            <Text className="mr-2">{formik.values?.treasure?.TrsrBd?.FinIndxs?.nm ?? formik.values?.treasure?.TrsrBdType?.nm ?? "-"}</Text>
                                        </View>

                                        {
                                            formik.errors.fee && formik.touched.fee && (
                                                <Text className="mt-2 text-rose-500">{formik.errors.fee}</Text>
                                            )
                                        }
                                    </View>
                                </View>
                            )
                            : null
                    }

                    <FormField
                        id="dueDate"
                        label="Qual o vencimento?"
                        hasError={!!formik.errors.dueDate && formik.touched.dueDate}
                        error={formik.errors.dueDate}
                    >
                        <Calendar
                            invalid={!!formik.errors.dueDate && formik.touched.dueDate}
                            value={formik.values.dueDate}
                            dateFormat="dd/mm/yy"
                            readOnlyInput
                            showIcon
                            disabledDays={[0, 6]}
                            onChange={(event) => {
                                formik.setFieldValue("dueDate", event.value)
                            }}
                            locale="pt"
                        />
                    </FormField>

                    <BankSearchSelector
                        hasError={!!formik.errors.negotiatingBank && formik.touched.negotiatingBank}
                        error={formik.errors.negotiatingBank}
                        selected={formik.values.negotiatingBank}
                        label="Onde você comprou?"
                        onSelectBank={(bank) => {
                            formik.setFieldValue("negotiatingBank", !bank ? null : { id: bank?._id, name: bank?.financial_inst_name });
                        }}
                    />

                    <FormField
                        id="purchaseDate"
                        label="Quando você comprou?"
                        hasError={!!formik.errors.purchaseDate && formik.touched.purchaseDate}
                        error={formik.errors.purchaseDate}
                    >
                        <Calendar
                            invalid={!!formik.errors.purchaseDate && formik.touched.purchaseDate}
                            value={formik.values.purchaseDate}
                            dateFormat="dd/mm/yy"
                            readOnlyInput
                            minDate={ASSET_MIN_PURCHASE_DATE}
                            icon={`${loadTaxMutation.isPending ? "pi pi-spin pi-spinner" : "pi pi-calendar"}`}
                            iconPos="right"
                            showIcon
                            onChange={async (event) => {
                                const date = event.value;
                                formik.setFieldValue("purchaseDate", date);
                                if (date) {
                                    await loadQuote(formik.values.treasure, date, { overwriteFee: true, overwritePurchaseValue: !isEdit });
                                } else if (!isReserva) {
                                    formik.setFieldValue("fee", "");
                                }
                            }}
                            locale="pt"
                        />
                    </FormField>

                    <Show when={!isReserva}>
                    <FormField
                        id="fee"
                        label="Taxa combinada no Tesouro Direto (% ao ano)"
                        hasError={!!formik.errors.fee && formik.touched.fee}
                        error={formik.errors.fee}
                    >
                        <InputNumber
                            className="w-full"
                            invalid={!!formik.errors.fee && formik.touched.fee}
                            value={formik.values.fee}
                            onValueChange={(e) => {
                                formik.setFieldValue("fee", e.value);
                            }}
                            suffix="%"
                            maxFractionDigits={2}
                            min={0}
                            max={200}
                        />
                    </FormField>

                    <FormField
                        id="unitValue"
                        label="Preço unitário do título (PU) na compra"
                        hasError={!!formik.errors.unitValue && formik.touched.unitValue}
                        error={formik.errors.unitValue}
                    >
                        <InputNumber
                            className="w-full"
                            invalid={!!formik.errors.unitValue && formik.touched.unitValue}
                            value={formik.values.unitValue}
                            disabled={loadTaxMutation.isPending}
                            onValueChange={(e) => {
                                formik.setFieldValue("unitValue", e.value ?? 0);
                            }}
                            mode="currency"
                            currency="BRL"
                            locale="pt-BR"
                        />
                    </FormField>
                    <Show when={loadTaxMutation.isPending}>
                        <Text className="text-sm text-500">Buscando a cotação do título nessa data…</Text>
                    </Show>
                    <Show when={!loadTaxMutation.isPending && quoteStatus === "found" && !!quoteDate}>
                        <Text className="text-sm text-500">
                            Cotação de {moment(quoteDate).format("DD/MM/YYYY")}. Ajuste se o seu extrato mostrar outro valor.
                        </Text>
                    </Show>
                    <Show when={!loadTaxMutation.isPending && quoteStatus === "missing"}>
                        <Text className="text-sm text-500">
                            Não encontramos a cotação deste título na data de compra. Informe o PU do seu extrato.
                        </Text>
                    </Show>
                    </Show>

                    <View flexColumn gap={2}>
                        <Text>Corretagem</Text>
                        <InputNumber
                            value={formik.values.operationCosts?.brokerage}
                            onValueChange={(e) =>
                                formik.setFieldValue("operationCosts", { ...formik.values.operationCosts, brokerage: e.value })
                            }
                            mode="currency"
                            currency="BRL"
                            locale="pt-BR"
                        />
                    </View>

                    <View flexColumn gap={2}>
                        <Text>Outros custos</Text>
                        <InputNumber
                            value={formik.values.operationCosts?.others}
                            onValueChange={(e) =>
                                formik.setFieldValue("operationCosts", { ...formik.values.operationCosts, others: e.value })
                            }
                            mode="currency"
                            currency="BRL"
                            locale="pt-BR"
                        />
                    </View>

                    <FormField
                        id="purchaseValue"
                        hasError={!!formik.errors.purchaseValue && formik.touched.purchaseValue}
                        error={formik.errors.purchaseValue}
                        label="Valor líquido de compra"
                    >
                        <InputNumber
                            invalid={!!formik.errors.purchaseValue && formik.touched.purchaseValue}
                            value={formik.values.purchaseValue}
                            onValueChange={(e) =>
                                formik.setFieldValue("purchaseValue", e.value)
                            }
                            mode="currency"
                            currency="BRL"
                            locale="pt-BR"
                        />
                    </FormField>

                    <FormField
                        id="totalValue"
                        label="Valor total de compra"
                    >
                        <InputNumber
                            disabled
                            value={(formik.values?.purchaseValue ?? 0) + (formik.values?.operationCosts?.brokerage ?? 0) + (formik.values?.operationCosts?.others ?? 0)}
                            mode="currency"
                            currency="BRL"
                            locale="pt-BR"
                        />
                    </FormField>

                    <View fullWidth>
                        <Button
                            onClick={() => {
                                formik.handleSubmit();
                                return Promise.resolve();
                            }}
                            className="w-full justify-content-center"
                            loading={upsertAssetMutation.isPending}
                            disabled={upsertAssetMutation.isPending || loadTaxMutation.isPending}
                        >
                            {isEdit ? "Editar" : "Cadastrar"}
                        </Button>
                    </View>
                </View>
            </Show>
        </>
    );
}

export default TreasureBoundsHandleForm;
